import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import Text from 'core/elements/Text'
import Tooltip from 'core/elements/tooltip/Tooltip'
import Theme from 'core/themes/model'

export type StatusVariant = 'success' | 'warning' | 'error' | 'disabled'

interface StatusCountItemProps {
  tooltip: string
  count: number
  variant: StatusVariant
  icon: string
  onClick?: () => void
}

const iconByVariant: Record<StatusVariant, string> = {
  success: 'circle-check',
  warning: 'circle-exclamation',
  error: 'triangle-exclamation',
  disabled: 'circle-minus',
}

const StatusCountItem = ({ tooltip, count, variant, icon, onClick }: StatusCountItemProps) => {
  const classes = useStyles()

  return (
    <Tooltip message={tooltip}>
      <div
        className={clsx(classes.iconWithCount, onClick && classes.clickable)}
        onClick={onClick}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={
          onClick
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onClick()
                }
              }
            : undefined
        }
      >
        <div className={classes.iconContainer}>
          <FontAwesomeIcon className={clsx(classes.statusIcon, classes[variant])}>
            {icon || iconByVariant[variant]}
          </FontAwesomeIcon>
        </div>
        <Text variant="body2">{count}</Text>
      </div>
    </Tooltip>
  )
}

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  iconWithCount: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
  },
  clickable: {
    cursor: 'pointer',
  },
  iconContainer: {
    background: theme.palette.grey[100],
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 32,
    width: 32,
  },
  statusIcon: {
    fontSize: '18px',
  },
  success: {
    background: theme.palette.graphGreen[100],
    color: theme.palette.graphGreen[500],
  },
  warning: {
    background: theme.palette.graphYellow[100],
    color: theme.palette.graphYellow[500],
  },
  error: {
    background: theme.palette.graphRed[100],
    color: theme.palette.graphRed[500],
  },
}))

export default StatusCountItem
